import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Avatar,
  Tooltip,
  Alert,
  Pagination,
  Stack,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Shield as ShieldIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useQueryState } from '../../hooks/useQueryState';

// ─── Severity styling ────────────────────────────────────────────────────────
// Kept intentionally separate from the indigo accent — severity is a
// safety signal, not a brand color. Each level pairs a color with a word
// so status is never conveyed by hue alone.
//
// This is the one "danger red" ramp for the safety surface (dot + chip
// bg/fg pairs are already contrast-checked); the page header's Shield icon
// below reuses SEVERITY_CONFIG.high.dot rather than a second, unrelated red
// (MUI's default error.main / #d32f2f, used elsewhere in the admin shell)
// so there's a single red family on screen, not two.
//
// Exported (along with STATUS_CONFIG/CATEGORY_LABELS/formatTimestamp below)
// so SafetyReviewPage.js — the review-in-context detail view this file's
// "Review" action now routes to instead of opening a dialog — renders the
// exact same severity/status/category language rather than a second,
// independently-drifting copy.
export const SEVERITY_CONFIG = {
  high: { color: 'error', label: 'High', dot: '#dc2626', bg: '#fee2e2', fg: '#991b1b' },
  medium: { color: 'warning', label: 'Medium', dot: '#d97706', bg: '#fef3c7', fg: '#92400e' },
  low: { color: 'default', label: 'Low', dot: adminColors.inkSoft, bg: '#f4f4f5', fg: adminColors.inkSoft },
};

// ─── Status styling ───────────────────────────────────────────────────────────
// ESCALATED and RESOLVED (Phase 2) get their own MUI `color`s rather than
// reusing PENDING's warning or REVIEWED's success — a triage list scanned
// quickly needs the five outcomes to read as five distinct chips, not two
// pairs that happen to differ only in label. See SafetyReviewPage's
// OUTCOME_OPTIONS for the matching decision-panel copy.
//
// `sx` (PENDING, RESOLVED only) repaints an outlined chip's border/text
// away from the MUI `color` keyword it'd otherwise resolve to, because that
// keyword fails WCAG AA at this chip's size against a white background:
//   warning.main (#ed6c02) → ~3.1:1 (same gap adminColors.warningAlertBg
//     already documents and fixes for filled Alerts elsewhere in this
//     theme — reused verbatim here, now for RESOLVED's outlined chip too).
//   info.main (#0288d1, MUI default — this theme doesn't override `info`)
//     → ~3.9:1, so RESOLVED uses adminColors.accentDark (the exact indigo
//     already used for "Pending" in InvitesPage's invite-status chip and
//     studentStatus.js's "Invited" chip) instead of MUI's `info` color.
// ESCALATED (error.main, this theme's own #dc2626) and REVIEWED
// (success.main #2e7d32) both clear 4.5:1 as MUI resolves them already, so
// they're left as plain `color` keywords with no `sx` override needed.
export const STATUS_CONFIG = {
  PENDING: { color: 'warning', label: 'Pending', sx: { color: adminColors.warningAlertBg, borderColor: adminColors.warningAlertBg } },
  ESCALATED: { color: 'error', label: 'Escalated' },
  REVIEWED: { color: 'success', label: 'Reviewed' },
  RESOLVED: { color: 'default', label: 'Resolved', sx: { color: adminColors.accentDark, borderColor: adminColors.accentDark } },
  DISMISSED: { color: 'default', label: 'Dismissed' },
};

const STATUS_TABS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'REVIEWED', label: 'Reviewed' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DISMISSED', label: 'Dismissed' },
  { value: '', label: 'All' },
];

const SORT_OPTIONS = [
  { value: 'oldest', label: 'Oldest first' },
  { value: 'newest', label: 'Newest first' },
];

// ─── Category label map ───────────────────────────────────────────────────────
export const CATEGORY_LABELS = {
  threats_violence: 'Threats & Violence',
  weapons: 'Weapons',
  bullying_harassment: 'Bullying & Harassment',
  profanity: 'Profanity',
  controlled_substances: 'Controlled Substances',
  sexual_misconduct: 'Sexual Misconduct',
  self_harm: 'Self-Harm',
  hate_speech: 'Hate Speech',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function formatTimestamp(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Every filter (plus sort/page) this view understands, doubling as
// useQueryState()'s schema. `sort` defaults to 'oldest' here — deliberately
// different from GET /api/safety/flags' own default of 'newest' — so a
// pending queue is worked oldest-first by default and nothing quietly ages
// out at the bottom of a "newest first" list; see fetchFlags below, which
// always sends `sort` explicitly rather than omitting it on the default.
//
// Every key here (not just `sort`) is now URL-held rather than local
// `useState`, on top of the phase-1 requirement to give `sort` a URL home:
// SafetyReviewPage's "breadcrumb back preserves tab/filters/sort" only
// works if that whole set actually lives in the query string this view
// hands the breadcrumb, not just one field of it.
const QUERY_DEFAULTS = {
  status: 'PENDING',
  severity: '',
  source: '',
  school: '',
  username: '',
  sort: 'oldest',
  page: 1,
};

// Quiet, secondary-filter field styling — visually recedes behind the
// status chip row, which leads the triage decision.
const quietFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.8125rem',
    backgroundColor: adminColors.surfaceRaised,
  },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

// ─── Conversation Dialog ─────────────────────────────────────────────────────
// Quick, read-only peek at a student's full conversation history — distinct
// from SafetyReviewPage's review-in-context view (a handful of messages
// around the flagged one, plus the decision panel). Kept as a dialog since
// "skim the whole history without leaving the triage list" is a different
// task from "make a decision on this one flag".

function ConversationDialog({ open, username, school, flagMessageId, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !username) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await authFetch(
          `${config.API_BASE_URL}/api/admin/users/${encodeURIComponent(username)}/conversations?page_size=200`
        );
        const data = await handleApiResponse(res, navigate);
        if (cancelled || data === null) return; // 401 — already redirected
        setMessages(data.messages || []);
      } catch (e) {
        if (!cancelled) setError(`Couldn't load this conversation. ${describeApiError(e)}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, username, navigate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          backgroundColor: '#18181b',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <PersonIcon />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {username}
            </Typography>
            {school && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {school}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small" aria-label="Close conversation">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, backgroundColor: adminColors.surface }}>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box p={3}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {!loading && !error && messages.length === 0 && (
          <Box p={3}>
            <Typography color="text.secondary" align="center">
              No messages found.
            </Typography>
          </Box>
        )}
        {!loading &&
          messages.map((msg) => {
            const isFlagged = msg.safety_flags && msg.safety_flags.length > 0;
            const isHighlighted = msg.id === flagMessageId;
            const isUser = msg.role === 'user';

            return (
              <Box
                key={msg.id}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: isHighlighted
                    ? '#fff3e0'
                    : isUser
                    ? 'white'
                    : '#f3f6fb',
                  borderLeft: isHighlighted ? '4px solid #ff6d00' : 'none',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      fontSize: 12,
                      bgcolor: isUser ? adminColors.accent : adminColors.aiAvatar,
                    }}
                  >
                    {isUser ? (username?.[0] || 'U').toUpperCase() : 'AI'}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {isUser ? username : 'Assistant'}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" ml="auto">
                    {formatTimestamp(msg.timestamp)}
                  </Typography>
                  {isHighlighted && (
                    <Chip
                      icon={<FlagIcon />}
                      label="Flagged message"
                      size="small"
                      color="warning"
                      sx={{ ml: 1 }}
                    />
                  )}
                  {msg.is_deleted && (
                    <Tooltip title="This message was part of a chat the student cleared. It is retained for review.">
                      <Chip
                        label="Deleted by student"
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, color: '#9e9e9e', borderColor: '#bdbdbd', fontSize: 11 }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="body2" sx={{ pl: 4.5, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {msg.content}
                </Typography>
                {isFlagged && !isHighlighted &&
                  msg.safety_flags.map((f) => (
                    <Box key={f.id} display="flex" gap={0.5} flexWrap="wrap" pl={4.5} mt={0.5}>
                      {(f.categories || []).map((cat) => (
                        <Chip
                          key={cat}
                          label={CATEGORY_LABELS[cat] || cat}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  ))}
              </Box>
            );
          })}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Triage card ──────────────────────────────────────────────────────────────
// The flagged message is the reason this view exists, so it gets full-width,
// legible space. Severity reads twice — a small dot and a color+word chip
// (never color-only) — and no more: a third signal (e.g. an accent border)
// would be redundant noise repeated down a long list.

function FlagCard({ flag, isMasterAdmin, onReview, onOpenConversation }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const messageRef = useRef(null);
  const sev = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.low;
  const statusCfg = STATUS_CONFIG[flag.status] || { label: flag.status, color: 'default' };
  const content = flag.message?.content || '';

  // Detect real clipping (scrollHeight vs clientHeight of the clamped
  // element) instead of guessing from character count, so "Show more"
  // appears exactly when the 3-line clamp actually cuts the text off —
  // at any viewport width, any font-size, any wrapping.
  useLayoutEffect(() => {
    const el = messageRef.current;
    if (!el || expanded) return undefined;
    const measure = () => setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, content]);

  const categoryText = (flag.categories || [])
    .map((cat) => CATEGORY_LABELS[cat] || cat)
    .join(', ');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: radius.surface,
        borderColor: 'rgba(24,24,27,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {/* Severity / status / source / timestamp */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            aria-hidden="true"
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sev.dot, flexShrink: 0 }}
          />
          <Chip
            label={sev.label}
            size="small"
            sx={{ bgcolor: sev.bg, color: sev.fg, fontWeight: 700, height: 22 }}
          />
        </Box>
        <Chip
          label={statusCfg.label}
          size="small"
          variant="outlined"
          color={statusCfg.color}
          sx={{ height: 22, ...statusCfg.sx }}
        />
        {/* "Someone already looked at this" — distinct from the status
            chip above, and only worth showing while a flag is still
            PENDING: once it's been decided (Reviewed/Escalated/Resolved/
            Dismissed), the status chip itself already answers "has this
            been handled". */}
        {flag.status === 'PENDING' && flag.viewed_at && (
          <Tooltip title={`Viewed by ${flag.viewed_by || 'someone'} · ${formatTimestamp(flag.viewed_at)}`}>
            <Chip
              icon={<VisibilityIcon sx={{ fontSize: 13 }} />}
              label="Viewed"
              size="small"
              variant="outlined"
              // Focusable so the "who and when" the Tooltip reveals isn't
              // mouse-only (D8) — without this, a keyboard user sees the
              // "Viewed" label but has no way to reach who or when.
              tabIndex={0}
              sx={{
                height: 22,
                fontSize: 11,
                color: adminColors.inkSoft,
                borderColor: 'rgba(24,24,27,0.16)',
                '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
              }}
            />
          </Tooltip>
        )}
        {flag.message?.source === 'agent_tutor' && (
          <Chip label="Agent Tutor" size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
        )}
        <Typography
          variant="caption"
          sx={{ ml: 'auto', color: adminColors.inkSoft, whiteSpace: 'nowrap' }}
        >
          {formatTimestamp(flag.created_at)}
        </Typography>
      </Box>

      {/* Flagged message — the primary content of the card */}
      <Box>
        <Typography
          ref={messageRef}
          variant="body2"
          sx={{
            color: adminColors.ink,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            ...(expanded
              ? {}
              : {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
          }}
        >
          &ldquo;{content || '—'}&rdquo;
        </Typography>
        {isOverflowing && (
          <Button
            onClick={() => setExpanded((v) => !v)}
            size="small"
            disableRipple
            sx={{
              mt: 0.25,
              px: 0.5,
              minHeight: 'auto',
              fontWeight: 600,
              fontSize: '0.75rem',
              '@media (pointer: coarse)': { minHeight: 44 },
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </Box>

      {/* Student · school · category metadata */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: adminColors.accent }}>
          {(flag.username?.[0] || 'U').toUpperCase()}
        </Avatar>
        <Typography variant="body2" fontWeight={600} sx={{ color: adminColors.ink }}>
          {flag.username}
        </Typography>
        {isMasterAdmin && flag.school && (
          <>
            <Box component="span" sx={{ color: adminColors.inkSoft }}>·</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <SchoolIcon sx={{ fontSize: 14, color: adminColors.inkSoft }} />
              <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                {flag.school}
              </Typography>
            </Box>
          </>
        )}
        {categoryText && (
          <>
            <Box component="span" sx={{ color: adminColors.inkSoft }}>·</Box>
            <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
              {categoryText}
            </Typography>
          </>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          mt: 0.5,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon fontSize="small" />}
          onClick={() => onOpenConversation(flag)}
          sx={{ width: { xs: '100%', sm: 'auto' }, ...TOUCH_HIT_SX }}
        >
          View conversation
        </Button>
        <Button
          variant={flag.status === 'PENDING' ? 'contained' : 'outlined'}
          size="small"
          color={flag.status === 'PENDING' ? 'error' : 'inherit'}
          onClick={() => onReview(flag)}
          aria-label={
            flag.status === 'PENDING'
              ? `Review flag for ${flag.username}`
              : `Update flag for ${flag.username}`
          }
          sx={{ width: { xs: '100%', sm: 'auto' }, ...TOUCH_HIT_SX }}
        >
          {flag.status === 'PENDING' ? 'Review' : 'Update'}
        </Button>
      </Box>
    </Paper>
  );
}

// ─── Loading skeleton — mirrors the card layout ───────────────────────────────

function FlagCardSkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: radius.surface,
        borderColor: 'rgba(24,24,27,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="rounded" width={64} height={22} />
        <Skeleton variant="rounded" width={78} height={22} />
        <Skeleton variant="text" width={90} sx={{ ml: 'auto' }} />
      </Box>
      <Skeleton variant="text" width="96%" />
      <Skeleton variant="text" width="88%" />
      <Skeleton variant="text" width="55%" />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={22} height={22} />
        <Skeleton variant="text" width={140} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
        <Skeleton variant="rounded" width={148} height={32} />
        <Skeleton variant="rounded" width={96} height={32} />
      </Box>
    </Paper>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────

// Per-tab copy (D4) — the Pending tab's own "nothing pending" vs. "all
// caught up" distinction used to be the only tab that told the truth about
// *why* the list is empty; every other tab shared one generic "no flags
// match these filters / try widening the filters" message even when no
// other filter was active at all — e.g. the Resolved tab with nothing else
// selected, where the honest reason is "nothing has been resolved yet",
// not "try a wider filter" (there isn't one to widen). `withFilters` is
// shown whenever a secondary filter (severity/source/username/school) is
// also narrowing the list; `noFilters` is what's true with the tab alone.
const STATUS_EMPTY_COPY = {
  PENDING: {
    title: 'No pending flags',
    withFilters: 'Nothing pending for this combination of filters.',
    noFilters: 'All caught up — nothing is waiting for review right now.',
  },
  ESCALATED: {
    title: 'No escalated flags',
    withFilters: 'Nothing escalated for this combination of filters.',
    noFilters: 'Nothing has been escalated.',
  },
  REVIEWED: {
    title: 'No reviewed flags',
    withFilters: 'Nothing reviewed for this combination of filters.',
    noFilters: 'Nothing has been marked as reviewed yet.',
  },
  RESOLVED: {
    title: 'No resolved flags',
    withFilters: 'Nothing resolved for this combination of filters.',
    noFilters: 'Nothing has been resolved yet.',
  },
  DISMISSED: {
    title: 'No dismissed flags',
    withFilters: 'Nothing dismissed for this combination of filters.',
    noFilters: 'Nothing has been dismissed yet.',
  },
  '': {
    title: 'No flags match these filters',
    withFilters: 'Try widening the status, severity, or source filters above.',
    noFilters: 'No safety flags have been recorded yet.',
  },
};

function EmptyQueueState({ statusFilter, hasOtherFilters, onClear }) {
  const copy = STATUS_EMPTY_COPY[statusFilter] || STATUS_EMPTY_COPY[''];
  const title = copy.title;
  const body = hasOtherFilters ? copy.withFilters : copy.noFilters;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: radius.surface,
        borderColor: 'rgba(24,24,27,0.08)',
        p: { xs: 4, sm: 6 },
        textAlign: 'center',
      }}
    >
      <ShieldIcon sx={{ fontSize: 36, color: adminColors.accent, opacity: 0.45, mb: 1.5 }} />
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: adminColors.ink }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: adminColors.inkSoft, mt: 0.5, maxWidth: 380, mx: 'auto' }}
      >
        {body}
      </Typography>
      {hasOtherFilters && (
        <Button
          variant="outlined"
          onClick={onClear}
          sx={{
            mt: 2.5,
            borderColor: adminColors.accent,
            color: adminColors.accent,
            fontWeight: 600,
            '&:hover': {
              borderColor: adminColors.accentDark,
              bgcolor: adminColors.accentLight,
            },
            ...TOUCH_HIT_SX,
          }}
        >
          Clear filters
        </Button>
      )}
    </Paper>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// Flag status is now saved from SafetyReviewPage (routed to from "Review"/
// "Update" below), not here — this view no longer takes an `onFlagsChanged`
// callback itself; AdminDashboardV2 passes that straight to
// SafetyReviewPage's own route instead. This component works standalone
// (e.g. in isolation/tests) with just `currentUser`.
const SafetyFlagsView = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [qs, setQueryState] = useQueryState(QUERY_DEFAULTS);

  const [flags, setFlags] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const PAGE_SIZE = 20;

  // Dialog state — conversation peek only; "Review"/"Update" now navigates
  // to SafetyReviewPage (see openReview below) instead of opening a dialog.
  const [convoFlag, setConvoFlag] = useState(null);
  const [convoDialogOpen, setConvoDialogOpen] = useState(false);

  // Always-visible pending count — independent of which status tab is
  // active, since "how many are unhandled" is the premise of a triage
  // view. null (not 0) means "unknown" so a failed request never lies.
  const [pendingCount, setPendingCount] = useState(null);

  // Monotonic request counter for fetchFlags below — belt-and-braces on
  // top of the AbortController cancellation there. `controller.abort()`
  // only cancels a request that's genuinely still in flight; if an older
  // request's response has already landed at the network layer by the
  // time a newer filter/tab change fires its cleanup, abort() on it is a
  // documented no-op and its `.then` still runs. Without this token, that
  // stale response can land *after* a newer, correct one and silently
  // overwrite `flags`/`total` with (e.g.) an empty "Reviewed" result while
  // the tab is showing "Pending" — exactly the empty-state-despite-
  // correct-data symptom this guards against.
  const fetchFlagsRequestIdRef = useRef(0);

  const isMasterAdmin =
    currentUser?.role === 'MASTER_ADMIN' || currentUser?.role === 'admin';

  // Free-text filters (username/school) are debounced before they reach the
  // URL/network — the fields themselves stay controlled/immediate so typing
  // feels responsive, but the fetch (and the query-string write) waits for
  // typing to settle. Mirrors FilterBar's own draft/debounce pattern (see
  // primitives/FilterBar.js) rather than importing it wholesale, since this
  // view's filter row layout (status ToggleButtonGroup leading, several
  // secondary Selects/fields following) doesn't fit FilterBar's shape.
  const [usernameDraft, setUsernameDraft] = useState(qs.username);
  const [schoolDraft, setSchoolDraft] = useState(qs.school);
  const debouncedUsernameDraft = useDebouncedValue(usernameDraft, 300);
  const debouncedSchoolDraft = useDebouncedValue(schoolDraft, 300);

  useEffect(() => { setUsernameDraft(qs.username); }, [qs.username]);
  useEffect(() => { setSchoolDraft(qs.school); }, [qs.school]);

  useEffect(() => {
    if (debouncedUsernameDraft === qs.username) return;
    setQueryState({ username: debouncedUsernameDraft, page: QUERY_DEFAULTS.page });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the debounced value should trigger this, not every qs/setQueryState identity change
  }, [debouncedUsernameDraft]);

  useEffect(() => {
    if (debouncedSchoolDraft === qs.school) return;
    setQueryState({ school: debouncedSchoolDraft, page: QUERY_DEFAULTS.page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSchoolDraft]);

  // ── Fetch flags ──────────────────────────────────────────────────────────
  // Returns an AbortController cleanup so a superseded request (e.g. the
  // user changes filters again before this one lands) is cancelled rather
  // than racing its response against a newer one — and see
  // fetchFlagsRequestIdRef above for the second, stronger guard against the
  // same class of problem when abort() itself can't help (response already
  // landed before cleanup ran).
  const fetchFlags = useCallback(() => {
    const controller = new AbortController();
    const requestId = ++fetchFlagsRequestIdRef.current;
    setLoading(true);
    setError('');

    const params = new URLSearchParams({ page: qs.page, page_size: PAGE_SIZE });
    if (qs.status) params.append('status', qs.status);
    if (qs.severity) params.append('severity', qs.severity);
    if (qs.source) params.append('source', qs.source);
    if (qs.username.trim()) params.append('username', qs.username.trim());
    if (qs.school.trim() && isMasterAdmin) params.append('school', qs.school.trim());
    // Always sent explicitly — the backend's own default ('newest') differs
    // from this view's default ('oldest'), so omitting it on a
    // default-sort request would silently ask for the wrong order.
    params.append('sort', qs.sort);

    (async () => {
      try {
        const res = await authFetch(
          `${config.API_BASE_URL}/api/safety/flags?${params.toString()}`,
          {},
          { signal: controller.signal }
        );
        const data = await handleApiResponse(res, navigate);
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        // A newer fetchFlags() call has since been issued (another filter/
        // tab change) — this response is stale even though it arrived
        // without an AbortError. Must never win over whatever the newer
        // request already rendered (or is about to).
        if (fetchFlagsRequestIdRef.current !== requestId) return;
        const newTotal = data.total || 0;
        setFlags(data.flags || []);
        setTotal(newTotal);

        // Reviewing the last flag on a page (or narrowing a filter) can
        // make the page the admin is currently on no longer exist once the
        // list shrinks - without this, that page renders as an empty queue
        // even though earlier pages still have flags waiting. Clamping here
        // (rather than only in a render-time check) re-triggers this same
        // fetchFlags via the `page` dependency below, so the corrected page
        // actually loads instead of just changing the number on the
        // Pagination control.
        const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
        if (qs.page > newTotalPages) {
          setQueryState({ page: newTotalPages });
        }
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        if (fetchFlagsRequestIdRef.current !== requestId) return;
        setError(`Couldn't load safety flags. ${describeApiError(err)}`);
      } finally {
        if (!controller.signal.aborted && fetchFlagsRequestIdRef.current === requestId) setLoading(false);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- qs is a single memoized object from useQueryState; its individual fields below are the real deps
  }, [qs.page, qs.status, qs.severity, qs.source, qs.username, qs.school, qs.sort, isMasterAdmin, navigate, setQueryState]);

  useEffect(() => {
    return fetchFlags();
  }, [fetchFlags]);

  // ── Pending count (always visible, independent of the active tab) ───────
  // A dedicated, lightweight fetch (page_size=1, only `total` is read) so
  // it stays accurate without waiting on whichever tab happens to be open.
  // Same debounce + abort treatment as fetchFlags — it's driven by the same
  // filter keystrokes.
  const fetchPendingCount = useCallback(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ status: 'PENDING', page: 1, page_size: 1 });
    if (qs.severity) params.append('severity', qs.severity);
    if (qs.source) params.append('source', qs.source);
    if (qs.username.trim()) params.append('username', qs.username.trim());
    if (qs.school.trim() && isMasterAdmin) params.append('school', qs.school.trim());

    (async () => {
      try {
        const res = await authFetch(
          `${config.API_BASE_URL}/api/safety/flags?${params.toString()}`,
          {},
          { signal: controller.signal }
        );
        const data = await handleApiResponse(res, navigate);
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        setPendingCount(typeof data.total === 'number' ? data.total : null);
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setPendingCount(null);
      }
    })();

    return () => controller.abort();
  }, [qs.severity, qs.source, qs.username, qs.school, isMasterAdmin, navigate]);

  useEffect(() => {
    return fetchPendingCount();
  }, [fetchPendingCount]);

  const hasOtherFilters = Boolean(qs.severity || qs.source || qs.username.trim() || qs.school.trim());

  // ── Open dialogs / navigate ──────────────────────────────────────────────
  // "Review"/"Update" now routes to /admin/safety/:flagId (SafetyReviewPage)
  // instead of opening a dialog — carrying the current query string so that
  // page's breadcrumb back preserves this exact tab/filters/sort.
  const openReview = (flag) => {
    navigate({ pathname: `/admin/safety/${flag.id}`, search: location.search });
  };

  const openConversation = (flag) => {
    setConvoFlag(flag);
    setConvoDialogOpen(true);
  };

  const clearFilters = () => {
    setQueryState({ ...QUERY_DEFAULTS });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <ShieldIcon sx={{ fontSize: 32, color: SEVERITY_CONFIG.high.dot }} />
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: adminColors.ink }}>
            Safety Monitoring
          </Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
            Flagged student messages requiring administrator review
          </Typography>
        </Box>
      </Box>

      {/* ── Triage bar: status chips lead, secondary filters recede ────────── */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={qs.status}
          exclusive
          onChange={(_, v) => {
            if (v !== null) {
              setQueryState({ status: v, page: QUERY_DEFAULTS.page });
            }
          }}
          aria-label="Filter by review status"
          sx={{
            bgcolor: adminColors.surface,
            borderRadius: radius.surface, // toolbar container — see radius.surface's own "toolbars" doc comment
            p: 0.5,
            gap: 0.5,
            flexWrap: 'wrap',
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              borderRadius: '8px !important',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              px: 1.5,
              color: adminColors.inkSoft,
              '&.Mui-selected': {
                bgcolor: '#fff',
                color: adminColors.ink,
                boxShadow: '0 1px 2px rgba(24,24,27,0.1)',
              },
              '&.Mui-selected:hover': { bgcolor: '#fff' },
              '&.Mui-focusVisible': {
                outline: `2px solid ${adminColors.accent}`,
                outlineOffset: 2,
              },
              ...TOUCH_HIT_SX,
            },
          }}
        >
          {STATUS_TABS.map((tab) => {
            const isPendingTab = tab.value === 'PENDING';
            // Pending always shows its own live count, whichever tab is
            // active. Other tabs only show a count while selected, reusing
            // the list's own `total` rather than firing extra requests.
            const count = isPendingTab
              ? pendingCount ?? (qs.status === 'PENDING' ? total : null)
              : qs.status === tab.value
              ? total
              : null;
            return (
              <ToggleButton
                key={tab.value || 'all'}
                value={tab.value}
                aria-label={`${tab.label} flags${count !== null ? `, ${count}` : ''}`}
              >
                {tab.label}
                {count !== null && (
                  <Box component="span" sx={{ ml: 0.75, opacity: 0.55, fontWeight: 600 }}>
                    {count}
                  </Box>
                )}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1.25}
          sx={{ mt: 1.5 }}
        >
          <FormControl size="small" sx={{ minWidth: 148, ...quietFieldSx }}>
            <InputLabel id="safety-sort-label">Sort</InputLabel>
            <Select
              labelId="safety-sort-label"
              label="Sort"
              value={qs.sort}
              onChange={(e) => setQueryState({ sort: e.target.value, page: QUERY_DEFAULTS.page })}
              renderValue={(v) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SortIcon sx={{ fontSize: 15, color: adminColors.inkSoft }} />
                  {SORT_OPTIONS.find((o) => o.value === v)?.label || v}
                </Box>
              )}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 128, ...quietFieldSx }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={qs.severity}
              label="Severity"
              onChange={(e) => setQueryState({ severity: e.target.value, page: QUERY_DEFAULTS.page })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 128, ...quietFieldSx }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={qs.source}
              label="Source"
              onChange={(e) => setQueryState({ source: e.target.value, page: QUERY_DEFAULTS.page })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="agent_tutor">Agent Tutor</MenuItem>
              <MenuItem value="chat">Chat</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Student username"
            value={usernameDraft}
            onChange={(e) => setUsernameDraft(e.target.value)}
            sx={{ minWidth: 160, ...quietFieldSx }}
          />

          {isMasterAdmin && (
            <TextField
              size="small"
              label="School"
              value={schoolDraft}
              onChange={(e) => setSchoolDraft(e.target.value)}
              sx={{ minWidth: 150, ...quietFieldSx }}
            />
          )}

          <Tooltip title="Clears the on-screen filters only — does not change any flags">
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ color: adminColors.inkSoft, fontWeight: 600, ...TOUCH_HIT_SX }}
            >
              Clear filters
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Triage list ──────────────────────────────────────────────────── */}
      {loading && (
        <Stack spacing={1.5} aria-busy="true" aria-label="Loading safety flags">
          {Array.from({ length: 4 }).map((_, i) => (
            <FlagCardSkeleton key={i} />
          ))}
        </Stack>
      )}

      {!loading && flags.length === 0 && (
        <EmptyQueueState
          statusFilter={qs.status}
          hasOtherFilters={hasOtherFilters}
          onClear={clearFilters}
        />
      )}

      {!loading && flags.length > 0 && (
        <Stack spacing={1.5}>
          {flags.map((flag) => (
            <FlagCard
              key={flag.id}
              flag={flag}
              isMasterAdmin={isMasterAdmin}
              onReview={openReview}
              onOpenConversation={openConversation}
            />
          ))}
        </Stack>
      )}

      {/* ── Pagination ───────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <Stack alignItems="center" mt={3}>
          <Pagination
            count={totalPages}
            page={qs.page}
            onChange={(_, v) => setQueryState({ page: v })}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <ConversationDialog
        open={convoDialogOpen}
        username={convoFlag?.username}
        school={convoFlag?.school}
        flagMessageId={convoFlag?.message_id}
        onClose={() => setConvoDialogOpen(false)}
      />
    </Box>
  );
};

export default SafetyFlagsView;
